import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useMemo, useState } from 'react';

// TierVar codes that are written per-Product in a formula (ex: [JUVEDERM_NET_SALES]). Mirrors TierVar.prodSpecific()
// on the backend - a small, rarely-changing fixed list, so it's kept here rather than adding a second backend round
// trip just to carry this one boolean per variable.
const PROD_SPECIFIC_VAR_CODES = new Set(['GROSS_SALES', 'NET_SALES', 'QTY']);

// Aggregated across the whole Program rather than tied to one Product, including the previous-period-max variants
const TOTAL_VAR_CODES = new Set([
  'TOTAL_GROSS_SALES',
  'TOTAL_NET_SALES',
  'TOTAL_QTY',
  'PREV_MAX_GROSS_SALES',
  'PREV_MAX_NET_SALES',
]);

// Set by the system rather than written into formulas by hand (Tier 0 is always [ACCESS]; [THRESHOLD] is added via
// the builder's own Threshold section), so they resolve to display names but are never offered in the palette
const NON_PALETTE_VAR_CODES = new Set(['ACCESS', 'THRESHOLD']);

// Cosmetic value formatting (currency/percent) for numbers compared against a variable; keyed by TierVar code,
// mirroring what each variable's aggCol/isCustProp actually represents on the backend.
const VAR_CODE_TO_FORMAT = {
  GROSS_SALES: 'CURRENCY',
  NET_SALES: 'CURRENCY',
  TOTAL_GROSS_SALES: 'CURRENCY',
  TOTAL_NET_SALES: 'CURRENCY',
  PREV_MAX_GROSS_SALES: 'CURRENCY',
  PREV_MAX_NET_SALES: 'CURRENCY',
  LOYALTY_PCT: 'PERCENT',
};

// What a payment basis is measured in (mirrors TierVar.forPymtBasis), and therefore what a Threshold formula's bare
// numbers are implicitly denominated in
export const PYMT_BASIS_VALUE_FORMAT = {
  NET_SALES: VAR_CODE_TO_FORMAT.NET_SALES,
  INCR_NET_SALES: VAR_CODE_TO_FORMAT.NET_SALES,
  QTY: VAR_CODE_TO_FORMAT.QTY,
};

const progProdGroupName = (progProd) => `${progProd.prodName || progProd.prodCode} (${progProd.prodCode})`;

/**
 * Builds the Tier formula palette entirely from the TierVar enum (via the generic ENUM_LIST_DATA_PLUGIN) crossed
 * with the Program's Program Products. Returns:
 * - groupedVariables: what the drag palette renders, a Map ordered currentProdCode's own Product group, then Totals,
 *   then other (non-Product-specific) variables, then every other Product's group
 * - codeToVariable: every resolvable variable (including the non-palette ACCESS/THRESHOLD), for rendering formulas
 */
export const useTierFormulaPalette = (progId, currentProdCode) => {
  const rsrc = useRsrc();
  const [tierVars, setTierVars] = useState([]);
  const [progProds, setProgProds] = useState([]);

  useEffect(() => {
    if (!progId) return;
    Promise.all([
      rsrc.data('md', 'ENUM_LIST_DATA_PLUGIN', { enumCode: 'TIER_VAR' }),
      rsrc.rows('md', 'PROG_PROD', { expr: ExprUtils.whereParams('progId', progId) }),
    ]).then(([vars, prods]) => {
      setTierVars(vars);
      setProgProds(prods);
    });
  }, [progId]);

  return useMemo(() => {
    // displayName is the short palette label (ex: "Gross Sales" under its own Product group); tokenLabel is what a
    // formula chip shows once dropped in, where the Product is no longer implied by a group header
    // (ex: "Juvederm Gross Sales")
    const toVariable = (tierVar, progProd) => ({
      code: progProd ? `${progProd.prodCode}_${tierVar.code}` : tierVar.code,
      displayName: tierVar.displayName,
      tokenLabel: progProd ? `${progProd.prodName || progProd.prodCode} ${tierVar.displayName}` : tierVar.displayName,
      format: VAR_CODE_TO_FORMAT[tierVar.code],
    });

    const prodSpecificVars = tierVars.filter((v) => PROD_SPECIFIC_VAR_CODES.has(v.code));
    const totalVars = tierVars.filter((v) => TOTAL_VAR_CODES.has(v.code));
    const otherVars = tierVars.filter(
      (v) => !PROD_SPECIFIC_VAR_CODES.has(v.code) && !TOTAL_VAR_CODES.has(v.code) && !NON_PALETTE_VAR_CODES.has(v.code),
    );
    const nonPaletteVars = tierVars.filter((v) => NON_PALETTE_VAR_CODES.has(v.code));

    const currentProgProd = progProds.find((p) => p.prodCode === currentProdCode);
    const otherProgProds = progProds.filter((p) => p.prodCode !== currentProdCode);

    const groupedVariables = new Map();
    if (currentProgProd) {
      groupedVariables.set(
        progProdGroupName(currentProgProd),
        prodSpecificVars.map((v) => toVariable(v, currentProgProd)),
      );
    }
    if (totalVars.length > 0)
      groupedVariables.set(
        'Totals',
        totalVars.map((v) => toVariable(v)),
      );
    if (otherVars.length > 0)
      groupedVariables.set(
        'Other',
        otherVars.map((v) => toVariable(v)),
      );
    otherProgProds.forEach((progProd) => {
      groupedVariables.set(
        progProdGroupName(progProd),
        prodSpecificVars.map((v) => toVariable(v, progProd)),
      );
    });

    const allVariables = [...[...groupedVariables.values()].flat(), ...nonPaletteVars.map((v) => toVariable(v))];
    const codeToVariable = Object.fromEntries(allVariables.map((v) => [v.code, v]));

    return { groupedVariables, codeToVariable };
  }, [tierVars, progProds, currentProdCode]);
};
